import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, KeyRound, Pencil, Plus, UserX } from 'lucide-react';
import { createUser, changeUserPassword, deactivateUser, fetchUsers, updateUser } from '../../api';
import { APP_TABS, pickKnownTabIds } from '../../config/navigation';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import { useToast } from '../../hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const defaultFormState = {
  username: '',
  password: '',
  role: 'user',
  allowedTabs: ['dashboard'],
  isActive: true
};

const TAB_LABELS = new Map(APP_TABS.map(({ id, label }) => [id, label]));

/**
 * Подписи разделов берутся с наложенным переименованием из «Настройки» → «Интерфейс»:
 * администратор должен узнавать разделы по тем же именам, что видят пользователи.
 */
const AllowedTabBadges = ({ tabs }) => {
  const panels = usePanelSettings();
  const knownTabs = pickKnownTabIds(tabs);

  if (knownTabs.length === 0) {
    return <span className="text-sm text-muted-foreground">Нет разрешённых вкладок</span>;
  }

  return knownTabs.map((tabId) => (
    <Badge key={tabId} variant="outline">
      {panels.tabLabel(tabId, TAB_LABELS.get(tabId))}
    </Badge>
  ));
};

const AccountsTab = () => {
  const { toast } = useToast();
  const panels = usePanelSettings();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingUserId, setEditingUserId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formState, setFormState] = useState(defaultFormState);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [passwordValue, setPasswordValue] = useState('');
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchUsers();
      setUsers(Array.isArray(response) ? response : []);
    } catch (error) {
      toast({
        title: 'Не удалось загрузить пользователей',
        description: error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const sortedUsers = useMemo(
    () => [...users].sort((left, right) => left.username.localeCompare(right.username, 'ru', { sensitivity: 'base' })),
    [users]
  );

  const resetDialog = useCallback(() => {
    setDialogMode('create');
    setEditingUserId(null);
    setDialogOpen(false);
    setFormState(defaultFormState);
  }, []);

  const openCreateDialog = () => {
    setDialogMode('create');
    setEditingUserId(null);
    setFormState(defaultFormState);
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setDialogMode('edit');
    setEditingUserId(user.id);
    setFormState({
      username: user.username,
      password: '',
      role: user.role,
      // Устаревшие id в форму не берём: повторное сохранение само вычищает их из прав.
      allowedTabs: pickKnownTabIds(user.allowedTabs),
      isActive: user.isActive
    });
    setDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setPasswordUser(user);
    setPasswordValue('');
    setPasswordDialogOpen(true);
  };

  const handleAllowedTabChange = (tabId, checked) => {
    setFormState((previous) => {
      const nextTabs = new Set(previous.allowedTabs);

      if (checked) {
        nextTabs.add(tabId);
      } else {
        nextTabs.delete(tabId);
      }

      return {
        ...previous,
        allowedTabs: Array.from(nextTabs)
      };
    });
  };

  const handleDialogSubmit = async (event) => {
    event.preventDefault();

    if (!formState.username.trim()) {
      toast({
        title: 'Введите логин',
        description: 'Имя пользователя обязательно.',
        variant: 'destructive'
      });
      return;
    }

    if (dialogMode === 'create' && !formState.password) {
      toast({
        title: 'Введите пароль',
        description: 'Для нового пользователя необходимо задать пароль.',
        variant: 'destructive'
      });
      return;
    }

    if (formState.role === 'user' && formState.allowedTabs.length === 0) {
      toast({
        title: 'Выберите вкладки',
        description: 'Обычному пользователю нужно назначить хотя бы одну доступную вкладку.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        username: formState.username.trim(),
        role: formState.role,
        allowedTabs: formState.role === 'admin' ? APP_TABS.map(({ id }) => id) : formState.allowedTabs,
        isActive: formState.isActive
      };

      if (dialogMode === 'create') {
        await createUser({
          ...payload,
          password: formState.password
        });
      } else if (editingUserId) {
        await updateUser(editingUserId, payload);
      }

      toast({
        title: dialogMode === 'create' ? 'Пользователь создан' : 'Пользователь обновлён',
        description: dialogMode === 'create'
          ? 'Новая учётная запись готова к использованию.'
          : 'Изменения прав и роли сохранены.'
      });

      resetDialog();
      await loadUsers();
    } catch (error) {
      toast({
        title: dialogMode === 'create' ? 'Не удалось создать пользователя' : 'Не удалось обновить пользователя',
        description: error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (!passwordUser?.id || !passwordValue) {
      return;
    }

    setIsPasswordSubmitting(true);
    try {
      await changeUserPassword(passwordUser.id, passwordValue);
      toast({
        title: 'Пароль обновлён',
        description: `Пароль для ${passwordUser.username} успешно изменён.`
      });
      setPasswordDialogOpen(false);
      setPasswordUser(null);
      setPasswordValue('');
    } catch (error) {
      toast({
        title: 'Не удалось сменить пароль',
        description: error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.',
        variant: 'destructive'
      });
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!userToDeactivate?.id) {
      return;
    }

    try {
      await deactivateUser(userToDeactivate.id);
      toast({
        title: 'Учётная запись деактивирована',
        description: `${userToDeactivate.username} больше не сможет войти в систему.`
      });
      setUserToDeactivate(null);
      await loadUsers();
    } catch (error) {
      toast({
        title: 'Не удалось деактивировать пользователя',
        description: error?.response?.data?.message ?? error?.message ?? 'Повторите попытку позже.',
        variant: 'destructive'
      });
    }
  };

  const roleDescription = formState.role === 'admin'
    ? 'Администратор получает полный доступ ко всем вкладкам и управлению учётными записями.'
    : 'Пользователь увидит только выбранные вкладки.';

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <CardTitle>Учётные записи</CardTitle>
          <CardDescription>
            Управляйте логинами, ролями, активностью и доступом к вкладкам интерфейса.
          </CardDescription>
        </div>
        <Button data-ev="settings.accounts.create" onClick={openCreateDialog} className="gap-2 md:self-start">
          <Plus className="h-4 w-4" />
          Создать пользователя
        </Button>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Пользователь</TableHead>
                <TableHead>Роль</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Вкладки</TableHead>
                <TableHead className="w-[240px] text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    Загружаем пользователей...
                  </TableCell>
                </TableRow>
              ) : sortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    Учётные записи ещё не созданы.
                  </TableCell>
                </TableRow>
              ) : (
                sortedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="font-medium">{user.username}</div>
                      <div className="text-xs text-muted-foreground">
                        Создан: {new Date(user.createdAtUtc).toLocaleString('ru-RU')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.role === 'admin' ? 'default' : 'outline'}>
                        {user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.isActive ? 'secondary' : 'destructive'}>
                        {user.isActive ? 'Активен' : 'Отключён'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.role === 'admin' ? (
                          <Badge variant="outline">Все вкладки</Badge>
                        ) : (
                          <AllowedTabBadges tabs={user.allowedTabs} />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2 max-md:flex-wrap max-md:justify-start">
                        <Button data-ev="settings.accounts.edit" variant="outline" size="sm" onClick={() => openEditDialog(user)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Редактировать
                        </Button>
                        <Button data-ev="settings.accounts.password" variant="outline" size="sm" onClick={() => openPasswordDialog(user)}>
                          <KeyRound className="mr-2 h-4 w-4" />
                          Пароль
                        </Button>
                        <Button
                          data-ev="settings.accounts.deactivate"
                          variant="destructive"
                          size="sm"
                          onClick={() => setUserToDeactivate(user)}
                          disabled={!user.isActive}
                        >
                          <UserX className="mr-2 h-4 w-4" />
                          Деактивировать
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : resetDialog())}>
        <DialogContent trackId="settings.accounts.edit_dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{dialogMode === 'create' ? 'Новый пользователь' : 'Редактирование пользователя'}</DialogTitle>
            <DialogDescription>
              {dialogMode === 'create'
                ? 'Задайте логин, пароль, роль и набор вкладок.'
                : 'Измените логин, роль, активность и доступные вкладки.'}
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-5" onSubmit={handleDialogSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="account-username">Логин</Label>
                <Input
                  id="account-username"
                  data-ev="settings.accounts.username_edit"
                  value={formState.username}
                  onChange={(event) => setFormState((previous) => ({ ...previous, username: event.target.value }))}
                  placeholder="Например, supervisor"
                />
              </div>

              {dialogMode === 'create' && (
                <div className="space-y-2">
                  <Label htmlFor="account-password">Пароль</Label>
                  <Input
                    id="account-password"
                    data-ev="settings.accounts.password_edit"
                    type="password"
                    value={formState.password}
                    onChange={(event) => setFormState((previous) => ({ ...previous, password: event.target.value }))}
                    placeholder="Введите пароль"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Роль</Label>
                <Select
                  trackId="settings.accounts.role"
                  trackValue
                  value={formState.role}
                  onValueChange={(value) => setFormState((previous) => ({
                    ...previous,
                    role: value,
                    allowedTabs: value === 'admin' ? APP_TABS.map(({ id }) => id) : previous.allowedTabs
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите роль" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Пользователь</SelectItem>
                    <SelectItem value="admin">Администратор</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-3 rounded-md border px-4 py-3 text-sm">
                  <Checkbox
                    data-ev="settings.accounts.active_toggle"
                    checked={formState.isActive}
                    onCheckedChange={(checked) => setFormState((previous) => ({ ...previous, isActive: checked === true }))}
                  />
                  Учётная запись активна
                </label>
              </div>
            </div>

            <div className="rounded-md border p-4">
              <p className="text-sm font-medium">Доступ к вкладкам</p>
              <p className="mt-1 text-sm text-muted-foreground">{roleDescription}</p>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {APP_TABS.map((tab) => {
                  const isChecked = formState.role === 'admin' || formState.allowedTabs.includes(tab.id);
                  return (
                    <label key={tab.id} className="flex items-start gap-3 rounded-md border p-3">
                      <Checkbox
                        data-ev="settings.accounts.tab_access_toggle"
                        checked={isChecked}
                        disabled={formState.role === 'admin'}
                        onCheckedChange={(checked) => handleAllowedTabChange(tab.id, checked === true)}
                      />
                      <div>
                        <p className="text-sm font-medium">{panels.tabLabel(tab.id, tab.label)}</p>
                        <p className="text-xs text-muted-foreground">{tab.id}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" data-ev="settings.accounts.dialog_cancel" variant="outline" onClick={resetDialog}>
                Отмена
              </Button>
              <Button type="submit" data-ev="settings.accounts.dialog_submit" disabled={isSubmitting}>
                {isSubmitting ? 'Сохраняем...' : dialogMode === 'create' ? 'Создать' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={passwordDialogOpen}
        onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) {
            setPasswordUser(null);
            setPasswordValue('');
          }
        }}
      >
        <DialogContent trackId="settings.accounts.password_dialog">
          <DialogHeader>
            <DialogTitle>Смена пароля</DialogTitle>
            <DialogDescription>
              Установите новый пароль для {passwordUser?.username ?? 'пользователя'}.
            </DialogDescription>
          </DialogHeader>

          <form className="space-y-4" onSubmit={handlePasswordSubmit}>
            <div className="space-y-2">
              <Label htmlFor="account-new-password">Новый пароль</Label>
              <Input
                id="account-new-password"
                data-ev="settings.accounts.new_password_edit"
                type="password"
                value={passwordValue}
                onChange={(event) => setPasswordValue(event.target.value)}
                placeholder="Введите новый пароль"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                data-ev="settings.accounts.password_cancel"
                variant="outline"
                onClick={() => {
                  setPasswordDialogOpen(false);
                  setPasswordUser(null);
                  setPasswordValue('');
                }}
              >
                Отмена
              </Button>
              <Button type="submit" data-ev="settings.accounts.password_submit" disabled={isPasswordSubmitting}>
                {isPasswordSubmitting ? 'Сохраняем...' : 'Сменить пароль'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(userToDeactivate)} onOpenChange={(open) => !open && setUserToDeactivate(null)}>
        <AlertDialogContent trackId="settings.accounts.deactivate_dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Деактивировать пользователя?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Пользователь {userToDeactivate?.username ?? 'unknown'} потеряет возможность входить в систему,
              но запись останется в базе.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-ev="settings.accounts.deactivate_cancel">Отмена</AlertDialogCancel>
            <AlertDialogAction data-ev="settings.accounts.deactivate_confirm" className="bg-destructive hover:bg-destructive/90" onClick={handleDeactivate}>
              Деактивировать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default AccountsTab;
